import {forwardRef} from 'react';

import {Tabs} from '@mui/material';
import {styled} from '@mui/system';

const ForwardRefTabs = forwardRef(
  ({children, ...props}, ref) => {
    return (<Tabs
      {...props}
      ref={ref}
    >
      {children}
    </Tabs>);
  },
);

const zmButtonBaseStyle = ({theme}) => `
&.MuiButtonBase-root {
  box-shadow: unset;
  text-transform: unset;
  &:hover: {
    box-shadow: unset,
  }
  &:active: {
    box-shadow: unset;
  }
  &.Mui-focusVisible {
    box-shadow: 0px 0px 0px 2px #FFFFFF, 0px 0px 0px 4px ${theme.palette.primary.swatch['60']};
  }
  &.Mui-disabled {
    background-color: ${theme.palette.common.swatch['20']};
    color: ${theme.palette.common.swatch['60']};
  }
}
`;

const headerStyle = ({theme}) => `
  &.MuiTabs-root {
    & .MuiTabs-flexContainer {
      gap: 32px;
    }
    & .MuiTabs-scroller {
      box-shadow: 0px -2px inset ${theme.palette.common.swatch['30']};
    }
    & .MuiTab-root {
      text-transform: none;
      font-size: 18px;
      line-height: 24px;
      font-weight: 500;
      padding-left: 0px;
      padding-right: 0px;
      color: ${theme.palette.common.swatch['70']};
      &:hover {
        color: ${theme.palette.common.swatch['80']};
      }
      &:active {
        color: ${theme.palette.common.swatch['90']};
      }
      &.Mui-selected {
        color: ${theme.palette.primary.swatch['60']};
      }
      &.Mui-disabled {
        color: ${theme.palette.common.swatch['50']};
        pointer: not-allowed;
      }
    }
    & .MuiTabs-indicator {
      border-radius: 2px;
    }
  }
`;

const chipStyle = ({theme}) => `
  &.MuiTabs-root {
    min-height: 32px;
    & .MuiTab-root {
      text-transform: none;
      font-size: 16px;
      line-height: 20px;
      font-weight: 400;
      min-height: 32px;
      color: ${theme.palette.common.swatch['70']};
      border-radius: 6px;
      padding: 4px 16px;
      margin-right: 4px;
      &:hover {
        background: ${theme.palette.common.swatch['20']};
      }
      &:active {
        background: ${theme.palette.common.swatch['30']};
      }
      &.Mui-selected {
        font-weight: 700;
        background: ${theme.palette.primary.swatch['10']};
        color: ${theme.palette.primary.swatch['60']};
      }
    }
    & .MuiTabs-indicator {
      display: none;
    }
  }
`;

const capsuleStyle = ({theme}) => `
  &.MuiTabs-root {
    min-height: 32px;
    & .MuiTab-root {
      text-transform: none;
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
      letter-spacing: .03em;
      min-height: 24px;
      min-width: 46px;
      color: ${theme.palette.text.primary};
      border-radius: 100px;
      border: 1px solid ${theme.palette.common.swatch['30']};
      padding: 2px 8px;
      margin-right: 8px;
      &:hover {
        background: ${theme.palette.common.swatch['20']};
      }
      &:active {
        background: ${theme.palette.common.swatch['30']};
      }
      &.Mui-selected {
        border: none;
        background: ${theme.palette.primary.main};
        color: ${theme.palette.common.white};
      }
    }
    & .MuiTabs-indicator {
      display: none;
    }
  }
`;

const chipStyleShareWindow = ({theme}) => `
  &.MuiTabs-root {
    min-height: 24px;
    border-radius: 6px;
    & .MuiTab-root {
      text-transform: none;
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
      min-height: 24px;
      color: ${theme.palette.common.swatch['70']};
      border-radius: 6px;
      padding: 1px;
      margin: 0;
      &:hover {
        background: ${theme.palette.common.swatch['20']};
      }
      &:active {
        background: ${theme.palette.common.swatch['30']};
      }
      &.Mui-selected {
        font-weight: 500;
        background: ${theme.palette.common.white};
        color: ${theme.palette.primary.swatch['60']};
        box-shadow: 0px 2px 4px 0px rgba(19, 22, 25, 0.10), 0px 4px 8px 0px rgba(19, 22, 25, 0.10);
        backdrop-filter: blur(5px);

      }
    }
    & .MuiTabs-indicator {
      display: none;
    }
  }
`;

const sliderStyle = ({theme}) => `
  &.MuiTabs-root {
    min-height: 24px;
    align-self: stretch;
    border-radius: 6px;
    background: ${theme.palette.common.swatch['10']};
    & .MuiTabs-scroller {
      padding: 1px;
    }
    & .MuiTabs-flexContainer {
      gap: 4px;
    }
    & .MuiTab-root {
      text-transform: none;
      font-size: 14px;
      line-height: 20px;
      font-weight: 450;
      min-height: 24px;
      flex: 1 1 0px;
      color: ${theme.palette.common.swatch['70']};
      border-radius: 5px;
      padding: 1px 4px;
      &:hover {
        background: ${theme.palette.common.swatch['20']};
      }
      &:active {
        background: ${theme.palette.common.swatch['30']};
      }
      &.Mui-selected {
        background: ${theme.palette.common.white};
        color: ${theme.palette.primary.swatch['60']};
        box-shadow: 0px 2px 4px 0px rgba(19, 22, 25, 0.1),
          0px 4px 8px 0px rgba(19, 22, 25, 0.1);

      }
    }
    & .MuiTabs-indicator {
      display: none;
    }
  }
`;

export const ZmHeaderTabs = styled(ForwardRefTabs, {shouldForwardProp: () => true})(
  (props) =>
    `
    ${zmButtonBaseStyle(props)}
    ${headerStyle(props)}
    `);

export const ZmChipTabs = styled(ForwardRefTabs, {shouldForwardProp: () => true})(
  (props) =>
    `
    ${zmButtonBaseStyle(props)}
    ${chipStyle(props)}
    `);

export const ZmCapsuleTabs = styled(ForwardRefTabs, {shouldForwardProp: () => true})(
  (props) =>
    `
    ${zmButtonBaseStyle(props)}
    ${capsuleStyle(props)}
    `);

export const ZmChipTabsShareWindow = styled(ForwardRefTabs, {shouldForwardProp: () => true})(
  (props) =>
    `
      ${zmButtonBaseStyle(props)}
      ${chipStyleShareWindow(props)}
      `);

export const ZmSliderTabs = styled(ForwardRefTabs, {shouldForwardProp: () => true})(
  (props) =>
    `
  ${zmButtonBaseStyle(props)}
  ${sliderStyle(props)}
  `
);

export const ZmSimpleTabs = styled(ForwardRefTabs)({
  [`.MuiTab-root`]: {
    padding: '0',
    textTransform: 'none',
    // lineHeight: '20px',
    minHeight: '32px',
    flexGrow: '1',
    fontWeight: 400,
  },
  minHeight: '32px',
  borderBottom: `1px solid #F1F4F6`,
});
